import { useState } from "react";
import { ALLERGENS } from "../../../shared/constants";
import { LABEL_MAX_PHOTOS, type LabelDraft } from "../../../shared/food-labels";
import type { FoodDto } from "../../../shared/foods";
import { NUTRIENTS } from "../../../shared/nutrition";
import { ApiRequestError } from "../../api/client";
import { foodApi } from "../../api/queries";
import { Modal } from "../../components/kit";
import { Alert } from "../../components/ui";
import { PHOTO_ACCEPT, photoFileError, preparePhoto } from "../../lib/photo";

// A product that is missing from the database, or whose shared data nobody checked: the restaurant photographs its
// label, the model proposes what is printed, the restaurant checks every field and saves (docs/BAZA_INGREDIENTE.md).

export const NUTRIENT_LABELS: Record<(typeof NUTRIENTS)[number], string> = {
  energy_kcal: "Valoare energetică (kcal)",
  fat: "Grăsimi (g)",
  saturated_fat: "din care saturate (g)",
  carbohydrates: "Glucide (g)",
  sugars: "din care zaharuri (g)",
  fiber: "Fibre (g)",
  protein: "Proteine (g)",
  salt: "Sare (g)",
};

const EMPTY: LabelDraft = {
  name: "",
  brand: null,
  producer: null,
  quantity: null,
  ean: null,
  ingredientsText: null,
  allergens: [],
  traces: [],
  additives: [],
  frozen: false,
  per100: Object.fromEntries(NUTRIENTS.map((n) => [n, null])) as LabelDraft["per100"],
};

const LABEL_PHOTO_SIDE = 2400;

export function LabelModal({
  rid,
  suggestedName,
  onSaved,
  onClose,
}: {
  rid: string;
  suggestedName?: string;
  onSaved: (product: FoodDto) => void;
  onClose: () => void;
}) {
  const api = foodApi(rid);
  const [photoIds, setPhotoIds] = useState<string[]>([]);
  const [draft, setDraft] = useState<LabelDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [additivesText, setAdditivesText] = useState("");

  const message = (e: unknown) =>
    e instanceof ApiRequestError || e instanceof Error ? e.message : "A apărut o eroare.";

  async function addPhotos(files: FileList | null) {
    if (!files?.length) return;
    setError(null);
    setBusy("Încarc fotografiile…");
    try {
      const ids = [...photoIds];
      for (const file of [...files].slice(0, LABEL_MAX_PHOTOS - ids.length)) {
        const bad = photoFileError(file);
        if (bad) throw new Error(bad);
        ids.push((await api.uploadLabelPhoto(await preparePhoto(file, LABEL_PHOTO_SIDE))).id);
      }
      setPhotoIds(ids);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  }

  function edit(next: LabelDraft) {
    setDraft(next);
    setAdditivesText(next.additives.join(", "));
  }

  async function read() {
    setError(null);
    setBusy("Citesc eticheta…");
    try {
      edit(await api.readLabel(photoIds));
    } catch (e) {
      // Reading is a help, not a condition: the restaurant can always fill in the label by hand.
      setError(`${message(e)} Poți completa datele de pe etichetă manual.`);
      edit({ ...EMPTY, name: suggestedName ?? "" });
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!draft) return;
    setError(null);
    setBusy("Salvez produsul…");
    try {
      const additives = additivesText
        .split(/[,\s]+/)
        .map((a) => a.trim().toUpperCase())
        .filter((a) => /^E\d{3,4}[A-Z]?$/.test(a));
      onSaved(await api.saveLabel({ ...draft, additives, photoIds }));
    } catch (e) {
      setError(message(e));
      setBusy(null);
    }
  }

  const set = (patch: Partial<LabelDraft>) => draft && setDraft({ ...draft, ...patch });
  const toggle = (key: "allergens" | "traces", code: (typeof ALLERGENS)[number][0]) =>
    draft &&
    set({ [key]: draft[key].includes(code) ? draft[key].filter((a) => a !== code) : [...draft[key], code] });

  return (
    <Modal
      title="Fotografiază eticheta"
      body={
        draft
          ? "Verifică fiecare câmp cu eticheta din mână și corectează ce nu se potrivește."
          : `Fă 1–${LABEL_MAX_PHOTOS} fotografii clare: fața produsului, tabelul nutrițional și lista de ingrediente.`
      }
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={!!busy}>
            Renunță
          </button>
          {draft ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={!!busy || !checked || !draft.name.trim() || !photoIds.length}
            >
              Salvează produsul
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={read}
              disabled={!!busy || !photoIds.length}
            >
              Citește eticheta
            </button>
          )}
        </>
      }
    >
      {error && <Alert tone="warning">{error}</Alert>}
      {busy && (
        <p className="muted" aria-live="polite">
          {busy}
        </p>
      )}
      {!draft && (
        <div className="field">
          <label className="field-label" htmlFor="lbl-photos">
            Fotografii ({photoIds.length}/{LABEL_MAX_PHOTOS})
          </label>
          <input
            id="lbl-photos"
            type="file"
            accept={PHOTO_ACCEPT}
            capture="environment"
            multiple
            disabled={!!busy || photoIds.length >= LABEL_MAX_PHOTOS}
            onChange={(e) => addPhotos(e.target.files)}
          />
          {/* The photos stay as the proof of what was declared: manual entry comes after at least one. */}
          {photoIds.length > 0 ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => edit({ ...EMPTY, name: suggestedName ?? "" })}
            >
              Completez manual, fără citire automată
            </button>
          ) : (
            <span className="muted caption">Fotografiile rămân ca dovadă a datelor declarate.</span>
          )}
        </div>
      )}
      {draft && (
        <div
          style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "55vh", overflowY: "auto" }}
        >
          {(
            [
              ["name", "Denumire"],
              ["brand", "Marcă"],
              ["quantity", "Cantitate (ex. 500 g)"],
              ["ean", "Cod de bare"],
            ] as const
          ).map(([key, label]) => (
            <div className="field" key={key}>
              <label className="field-label" htmlFor={`lbl-${key}`}>
                {label}
              </label>
              <input
                id={`lbl-${key}`}
                className="field-input"
                value={draft[key] ?? ""}
                onChange={(e) => set({ [key]: key === "name" ? e.target.value : e.target.value || null })}
              />
            </div>
          ))}
          <div className="field">
            <label className="field-label" htmlFor="lbl-ingr">
              Ingrediente (cum sunt tipărite)
            </label>
            <textarea
              id="lbl-ingr"
              className="field-input"
              rows={3}
              value={draft.ingredientsText ?? ""}
              onChange={(e) => set({ ingredientsText: e.target.value || null })}
            />
          </div>
          {(
            [
              ["allergens", "Alergeni (din ingrediente sau „Conține”)"],
              ["traces", "Poate conține urme de"],
            ] as const
          ).map(([key, legend]) => (
            <fieldset key={key} style={{ border: 0, margin: 0, padding: 0 }}>
              <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>
                {legend}
              </legend>
              <div className="check-grid">
                {ALLERGENS.map(([code, label]) => (
                  <label key={code} className="checkline">
                    <input
                      type="checkbox"
                      checked={draft[key].includes(code)}
                      onChange={() => toggle(key, code)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
          <div className="field">
            <label className="field-label" htmlFor="lbl-add">
              Aditivi (E-uri, separați prin virgulă)
            </label>
            <input
              id="lbl-add"
              className="field-input"
              value={additivesText}
              onChange={(e) => setAdditivesText(e.target.value)}
            />
          </div>
          <label className="checkline">
            <input type="checkbox" checked={draft.frozen} onChange={() => set({ frozen: !draft.frozen })} />
            Produs congelat
          </label>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>
              Valori nutriționale la 100 g (sau 100 ml)
            </legend>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 6, alignItems: "center" }}>
              {NUTRIENTS.map((n) => (
                <label key={n} style={{ display: "contents" }}>
                  <span>{NUTRIENT_LABELS[n]}</span>
                  <input
                    className="field-input"
                    inputMode="decimal"
                    value={draft.per100[n] ?? ""}
                    onChange={(e) => {
                      const v = e.target.value.replace(",", ".").trim();
                      const num = v === "" ? null : Number(v);
                      set({
                        per100: {
                          ...draft.per100,
                          [n]: num === null || Number.isFinite(num) ? num : draft.per100[n],
                        },
                      });
                    }}
                  />
                </label>
              ))}
            </div>
          </fieldset>
          <label className={`confirm${checked ? " ok" : ""}`}>
            <input type="checkbox" checked={checked} onChange={() => setChecked(!checked)} />
            <strong style={{ fontWeight: 600 }}>Am verificat datele cu eticheta produsului.</strong>
          </label>
        </div>
      )}
    </Modal>
  );
}
